/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import {
  Box, Button, DialogActions, DialogContent,
  Checkbox, FormControlLabel, Alert, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { organizationPrimaryColors } from "../../../../layouts/PermanentSidebarMUI";
import { useOrgan } from "../../../../hooks/useOrgan";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useBaseService from "../../../../hooks/services/useBaseService";
import useLoader from "../../../../hooks/useLoader";
import { entities } from "../../../../constants/entities";
import { useCallback, useState, useEffect } from "react";
import { scannerEquipmentFields } from "../../../../constants/forms/equipmentFields";
import useForm from "../../../../hooks/useForm";
import { setDefaultFieldValues } from "../../../../utils/objectUtils";
import Form from "../../../../components/forms/Form";
import SimpleBody from "../../../../components/forms/SimpleBody";
import useRemoteSelectOptions from "../../../../hooks/useRemoteSelectOptions";
import { EquipmentEnum } from "../../../../enums/equipment-enum";

//Lista de sataus a serem confirmados
const scannableStatusIds = [
  EquipmentEnum.RETURNED,
  EquipmentEnum.REMOVED_FROM_SHIPMENT,
]

export const ReturnConfirmationModal = ({
  data,
  open,
  onClose,
  onClear,
  hasMixedStatuses = false,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 🆕 Estado do checkbox de confirmação
  const [confirmChecked, setConfirmChecked] = useState(false);

  const [_, returnConfirmationSection] = scannerEquipmentFields;

  const { formData, handleChange, resetForm, setFormData } = useForm(
    setDefaultFieldValues([returnConfirmationSection]),
    [returnConfirmationSection],
  );

  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { hideLoader, showLoader } = useLoader();

  const { selectedOrgan } = useOrgan();
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM;

  // 🆕 Reset do checkbox ao fechar/abrir o modal
  useEffect(() => {
    if (!open) setConfirmChecked(false);
  }, [open]);

  // 🆕 Contador de equipamentos em devolução
  const returnedCount =
  data?.filter((item) => item.status_id === EquipmentEnum.RETURNED).length || 0

  const removedFromShipmentCount =
    data?.filter(
      (item) => item.status_id === EquipmentEnum.REMOVED_FROM_SHIPMENT,
    ).length || 0

  const scannableEquipments =
    data?.filter((item) => scannableStatusIds.includes(item.status_id)) || []

  const driverSelect = useRemoteSelectOptions({
    queryKey: ["drivers"],
    fieldId: "return_confirmations.driver_id",
    baseServiceGet,
    entityPath: entities.users.get,
    extraFilters: {
      // TODO: Trocar por um enum
      "roles.id": {
        $eq: 17, // Motorista
      },
    },
    formData,
    setFormData,
    mapToOption: ({ id, name }) => ({ value: id, label: name }),
    enablePagination: true,
    perPage: 15,
    immediateFetch: true,
    initialPage: 1,
  });

  const selects = {
    "return_confirmations.driver_id": driverSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects],
  );
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects],
  );

  const refreshList = useCallback(() => {
    queryClient.invalidateQueries(["equipments"]);
  }, [queryClient]);

  const saveReturnConfirmations = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePost(
        entities.returnConfirmations.create,
        data,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        hideLoader();
        resetForm();
        setConfirmChecked(false); // 🆕 Reset do checkbox após sucesso
        onClear();
        queryClient.invalidateQueries(["equipments"]);
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleClose();
    },
  });

  const handleSubmit = async () => {
    // 🆕 Bloqueio de segurança: só executa se checkbox estiver marcado
    if (!confirmChecked) return;

    try {
      if (!data) return;

      const payload = {
        return_confirmations: {
          driver_id: formData.return_confirmations.driver_id?.value,
          details: formData.return_confirmations.details,
        },
        items: scannableEquipments.map((item) => ({
          equipment_id: item.id,
          movement_id: item.movement_id,
          movement_item_id: item.movement_item_id,
        })),
      }
      await saveReturnConfirmations.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao confirmar equipamentos:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleClose = () => {
    onClose();
    resetForm();
    setConfirmChecked(false); // 🆕 Reset do checkbox ao fechar
  };

  // 🆕 Lógica do botão Confirmar: precisa de motorista + checkbox marcado
  const isConfirmDisabled =
    !confirmChecked || !formData.return_confirmations?.driver_id?.value;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: primaryColor,
          color: "#FFFFFF",
          py: 2,
          px: 3,
        }}>
        <DialogTitle sx={{ fontWeight: "bold", p: 0 }}>
          Confirmar equipamentos escaneados
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Form
          onSubmit={handleSubmit}
          textSubmit="Confirmar"
          initialFormData={formData}
          textLoadingSubmit="Confirmando..."
          showButtons={false}>
          {() => (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <SimpleBody
                fields={returnConfirmationSection.fields}
                formData={formData}
                handleFieldChange={handleChange}
                formErrors={formErrors}
                getOptions={getOptions}
                getSelectedValue={getSelectedValue}
                getSelectMeta={getSelectMeta}
              />

              {/* Alerta de lista mista */}
              {hasMixedStatuses && (
                <Alert severity="info">
                  <Typography variant="body2">
                    O lote contém equipamentos em devolução e retirados do carregamento.
                    Cada equipamento seguirá o fluxo correspondente ao seu status.
                  </Typography>
                </Alert>
              )}

              {/* Contagem por tipo de equipamento */}
              {(returnedCount > 0 || removedFromShipmentCount > 0) && (
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>{returnedCount}</strong> equipamento(s) em devolução irão para
                    manutenção e <strong>{removedFromShipmentCount}</strong> retirado(s) do
                    carregamento serão liberados para locação.
                  </Typography>
                </Alert>
              )}

              {/* 🆕 Checkbox de confirmação */}
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  backgroundColor: "#f9f9f9",
                }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmChecked}
                      onChange={(e) => setConfirmChecked(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Confirmo que desejo prosseguir com a confirmação em massa
                      de todos os equipamentos listados. Esta ação
                      não pode ser desfeita.
                    </Typography>
                  }
                />
              </Box>
            </Box>
          )}
        </Form>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          variant="outlined"
          sx={{ minWidth: 100 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isConfirmDisabled} // 🆕 Desabilitado até marcar o checkbox + motorista
          sx={{ minWidth: 100 }}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};